import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, UserCircle, Phone, FirstAid, PawPrint } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { useApi, errMsg, responderUrl } from "../lib/api";
import { GROUP_TYPES } from "../lib/constants";
import { useMe } from "../components/AppShell";
import { PageHeader, Completeness, Empty } from "../components/common";
import { useCart } from "../lib/site";
import { AddPetDialog, petGroups } from "../components/AddPet";
import { useBilling } from "../lib/billing";

function PhoneCard() {
  const api = useApi();
  const { me, setMe } = useMe();
  const [phone, setPhone] = useState(me.phone || "");
  const save = async () => {
    try { const r = await api.patch("/me", { phone }); setMe(r.data); toast.success("WhatsApp number saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-5 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="phone-card">
      <div className="flex-1"><div className="overline mb-2 flex items-center gap-2"><Phone size={14} /> Your WhatsApp number for alerts</div>
        <Input data-testid="my-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" /></div>
      <Button data-testid="save-phone-btn" onClick={save}>Save</Button>
    </div>
  );
}

function NewPerson({ onDone }) {
  const api = useApi();
  const [f, setF] = useState({ first_name: "", last_name: "", is_minor: false });
  const create = async () => {
    try { const r = await api.post("/people", f); onDone(r.data.id); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel p-4 grid sm:grid-cols-[1fr_1fr_auto_auto] gap-3 items-center" data-testid="new-person-form">
      <Input data-testid="new-person-first-name" placeholder="First name" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} />
      <Input data-testid="new-person-last-name" placeholder="Last name" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} />
      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="new-person-minor" checked={f.is_minor} onCheckedChange={(v) => setF({ ...f, is_minor: !!v })} />Under 18</label>
      <Button data-testid="new-person-submit" disabled={!f.first_name} onClick={create}>Create</Button>
    </div>
  );
}

function TrialBanner({ groups }) {
  const b = useBilling();
  const trial = groups.filter((g) => g.sub_state === "trial" && g.subscription_until);
  if (trial.length) return (<div className="mb-8 space-y-2" data-testid="trial-banner">{trial.map((g) => {
    const days = Math.max(0, Math.ceil((new Date(g.subscription_until) - Date.now()) / 86400000));
    return <div key={g.id} className="panel p-4 border-sky-500/40 text-sm flex flex-wrap justify-between gap-2" data-testid={`trial-banner-${g.id}`}>
      <span><b className="text-sky-300">Free trial</b> · {g.name} · {days} day{days === 1 ? "" : "s"} left</span><span className="text-slate-400">Order a card or band to keep going after the trial.</span></div>;
  })}</div>);
  if (!groups.length && b?.trial_days > 0) return <div className="mb-8 panel p-4 border-sky-500/40 text-sm" data-testid="trial-welcome"><b className="text-sky-300">Your first {b.trial_days} days are free.</b> Create a family, school, company or solo plan to start your free trial.</div>;
  return null;
}

export default function Home() {
  const api = useApi();
  const nav = useNavigate();
  const { me, setMe, groups } = useMe();
  const [people, setPeople] = useState([]);
  const [adding, setAdding] = useState(false);
  const { count: cartCount } = useCart();
  useEffect(() => {
    const pending = localStorage.getItem("bsafe_invite");
    if (pending) { localStorage.removeItem("bsafe_invite"); nav(`/invite/${pending}`); return; }
    const pendingPhone = localStorage.getItem("bsafe_phone");
    if (pendingPhone) {
      localStorage.removeItem("bsafe_phone");
      let p = null;
      try { p = JSON.parse(pendingPhone); } catch {}
      if (p?.phone && Date.now() - p.at < 3600000 && !me.phone) api.patch("/me", { phone: p.phone }).then((r) => setMe(r.data));
    }
    api.get("/people").then((r) => setPeople(r.data));
  }, [api, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="home-page">
      <PageHeader overline="Welcome" title={`Hello${me.name ? `, ${me.name.split(" ")[0]}` : ""}`}>
        <Button data-testid="home-my-page-btn" variant="outline" onClick={() => nav("/app/my-page")}><FirstAid size={16} className="mr-1" />My emergency page</Button>
        <Button data-testid="home-new-group-btn" onClick={() => nav("/app/onboarding")}><Plus size={16} className="mr-1" />New group</Button>
      </PageHeader>
      {!me.phone && <div className="mb-8"><PhoneCard /></div>}
      <TrialBanner groups={groups} />
      {cartCount > 0 && <Link to="/app/checkout" data-testid="home-cart-banner" className="mb-8 flex items-center justify-between panel p-4 border-red-500/50 hover:border-red-400">
        <span className="text-sm">You have {cartCount} item{cartCount > 1 ? "s" : ""} in your shop cart.</span><span className="text-sm font-semibold text-red-400">Checkout →</span></Link>}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-4"><h2 className="text-lg font-bold">Profiles I manage</h2>
          <div className="flex gap-2"><AddPetDialog groups={petGroups(groups)} testid="home-add-pet-btn" />
            <Button variant="outline" size="sm" data-testid="add-profile-btn" onClick={() => setAdding(!adding)}>Add profile</Button></div></div>
        {adding && <div className="mb-4"><NewPerson onDone={(id) => nav(`/app/profile/${id}`)} /></div>}
        {people.length === 0 ? <Empty testid="no-profiles">No profiles yet. Accept a WhatsApp invite or add a profile for yourself or a child.</Empty> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{people.map((p) => (
            <div key={p.id} className="panel p-5 lift hover:border-slate-500"><Link to={`/app/profile/${p.id}`} data-testid={`profile-card-${p.id}`} className="block">
              <div className="flex items-center gap-3">{p.kind === "pet" ? <PawPrint size={34} className="text-amber-300" data-testid={`pet-icon-${p.id}`} /> : <UserCircle size={34} className="text-slate-500" />}
                <div><div className="font-semibold">{p.first_name} {p.last_name}{p.lost && <span className="ml-2 text-[10px] font-bold rounded-full bg-red-600 px-2 py-0.5 align-middle" data-testid={`pet-lost-badge-${p.id}`}>LOST</span>}</div><div className="text-xs text-slate-400">{p.groups.map((g) => g.name).join(" · ") || "No groups"}</div></div></div>
              <div className="mt-4"><Completeness value={p.completeness} /></div>
              {!p.consent_at && <div className="mt-3 text-xs text-amber-300">{p.kind === "pet" ? "Setup needed – consent and pet details" : "Setup needed – consent and medical info"}</div>}
            </Link>
            {p.personal_token && <a href={responderUrl(p.personal_token, "preview")} target="_blank" rel="noreferrer" data-testid={`profile-card-page-${p.id}`}
              className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-red-400 hover:text-red-300"><FirstAid size={14} />{p.kind === "pet" ? "Found-pet page" : "My emergency page"}</a>}
            </div>))}</div>)}
      </section>
      <section>
        <h2 className="text-lg font-bold mb-4">My groups</h2>
        {groups.length === 0 ? <Empty testid="no-groups">You don't manage any groups yet. Create one to invite members and order tags.</Empty> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{groups.map((g) => (
            <Link key={g.id} to={g.my_role === "nurse" ? `/app/g/${g.id}/nurse` : `/app/g/${g.id}`} data-testid={`group-card-${g.id}`}
              className="panel p-5 lift block" style={{ borderTop: `3px solid ${GROUP_TYPES[g.type]?.color}` }}>
              <div className="overline" style={{ color: GROUP_TYPES[g.type]?.color }}>{GROUP_TYPES[g.type]?.label} · {g.my_role}</div>
              <div className="font-display font-bold text-xl mt-2">{g.name}</div>
              <div className="text-sm text-slate-400 mt-1">{g.member_count} {g.member_count === 1 ? GROUP_TYPES[g.type]?.member : GROUP_TYPES[g.type]?.members.toLowerCase()}</div>
            </Link>))}</div>)}
      </section>
    </div>
  );
}
